namespace advisorop.components.identity {

    /// Blocks characters that cannot appear in the field as they are typed or pasted, rather than
    /// masking the input. The user still sees and edits a plain text box; they simply cannot get an
    /// impossible character into it. Everything is validated again on the server regardless.
    class AllowedCharsDirective implements ng.IDirective {
        require = "ngModel";
        restrict = "A";
        scope = false;

        link(scope: ng.IScope, element: ng.IAugmentedJQuery, attrs: any, ngModel: ng.INgModelController) {
            const pattern = attrs.allowedChars === "email"
                ? /[\s<>(),;:\\"\[\]]/g          // whitespace and the RFC specials nobody types on purpose
                : /[^0-9 ()\-.+]/g;              // phone: digits and the punctuation people write them with

            ngModel.$parsers.unshift((value: string) => {
                if (!value) {
                    return value;
                }
                const cleaned = value.replace(pattern, "");
                if (cleaned !== value) {
                    ngModel.$setViewValue(cleaned);
                    ngModel.$render();
                }
                return cleaned;
            });
        }
    }

    angular.module(Constants.APP).directive("allowedChars", () => new AllowedCharsDirective());

    class TwoFactorFormController {

        $router;
        setup: domains.identity.ITwoFactorSetup = {};
        loaded = false;

        /// True while an administrator is impersonating this login. Everything on this panel is
        /// the account holder's own setting - the server refuses an impersonator on every action
        /// here - so the fields and the switch are hidden rather than offered and then refused.
        impersonated = false;

        phoneInput = "";
        phoneError: string = null;
        laddaPhone = false;

        emailInput = "";
        emailError: string = null;
        laddaEmail = false;

        laddaEnable = false;

        constructor(
            private identityService: services.IdentityService,
            private gs: services.GeneralService) {
        }

        // Validation lives in domains.identity.ContactValidator, shared with the servicer edit
        // screen. It mirrors ContactValidation on the server, which remains the authority.
        // These two stay as methods because the template binds to them.

        /// Returns an error message, or null when the number is acceptable.
        validateUsPhone(raw: string): string {
            return domains.identity.ContactValidator.usPhone(raw);
        }

        /// Returns an error message, or null when the address is acceptable.
        validateEmail(raw: string): string {
            return domains.identity.ContactValidator.email(raw);
        }

        onPhoneChanged(): void {
            this.phoneError = this.phoneInput ? this.validateUsPhone(this.phoneInput) : null;
        }

        onEmailChanged(): void {
            this.emailError = this.emailInput ? this.validateEmail(this.emailInput) : null;
        }

        /// Tidies what the user typed into the shape it will be shown in, once it is valid.
        formatPhoneOnBlur(): void {
            if (this.phoneInput && !this.validateUsPhone(this.phoneInput)) {
                this.phoneInput = domains.identity.ContactValidator.formatUsPhone(this.phoneInput);
            }
        }

        savePhone(): void {
            this.phoneError = this.validateUsPhone(this.phoneInput);
            if (this.phoneError) {
                return;
            }

            this.laddaPhone = true;
            this.identityService.setMyTwoFactorPhone(this.phoneInput)
                .then(response => {
                    this.setup = response.data || {};
                    this.phoneInput = "";
                    this.phoneError = null;
                    toastr.info("Your mobile number has been saved.");
                }, err => {
                    // The server is the authority; show exactly what it said.
                    this.phoneError = (err && err.data) ? err.data : "The number could not be saved.";
                })
                .finally(() => { this.laddaPhone = false; });
        }

        saveEmail(): void {
            this.emailError = this.validateEmail(this.emailInput);
            if (this.emailError) {
                return;
            }

            this.laddaEmail = true;
            this.identityService.setMyTwoFactorEmail(this.emailInput)
                .then(response => {
                    this.setup = response.data || {};
                    this.emailInput = "";
                    this.emailError = null;
                    toastr.info("Your email address has been saved.");
                }, err => {
                    this.emailError = (err && err.data) ? err.data : "The address could not be saved.";
                })
                .finally(() => { this.laddaEmail = false; });
        }

        /// A user can switch their own second factor on but not off. Turning it off is an
        /// administrator action, so this form only ever offers the "on" direction.
        enableTwoFactor(): void {
            if (!this.setup.canEnable) {
                return;
            }

            if (!window.confirm("Turn on two-factor authentication?\n\nFrom your next sign-in you will be asked for a security code as well as your password. Only an administrator can turn this off again."))
                return;

            this.laddaEnable = true;
            this.identityService.enableMyTwoFactor()
                .then(() => {
                    this.setup.twoFactorEnabled = true;
                    angular.element(document.body).removeClass("has-2fa-bar");
                    toastr.info("Two-factor authentication is now on for your account.");
                }, err => this.gs.showError(err))
                .finally(() => { this.laddaEnable = false; });
        }

        $onInit(): void {
            // Whose sign-in this panel describes decides whether it can be used at all, so it is
            // read alongside the setup. On failure the panel stays editable: the server is the
            // authority and refuses an impersonator regardless of what the page believes.
            this.identityService.getUserPromise().then(user => {
                this.impersonated = !!user.impersonated;
            }, err => console.warn("Two-factor panel: could not identify the signed-in user.", err));

            this.identityService.getMyTwoFactorSetup()
                .then(response => {
                    this.setup = response.data || {};
                    this.loaded = true;
                }, err => this.gs.showError(err));
        }

        $routerOnActivate(next, previous) {
        }
    }

    class TwoFactorForm implements ng.IComponentOptions {
        public bindings: any;
        public controller: any;
        public controllerAs: string;
        public templateUrl: string;

        constructor() {
            this.bindings = {
                $router: "<"
            };
            this.controller = ["identityService", "generalService", TwoFactorFormController];
            this.controllerAs = "vm";
            this.templateUrl = 'spa/shared/identity-module/components/two-factor-form.html';
        }
    }
    angular.module(Constants.APP).component("twoFactorForm", new TwoFactorForm());
}
